// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Local microphone capture and audio-level metering. A call wants the browser's
 * echo-cancellation / noise-suppression / auto-gain ON (unlike the measurement
 * tools in this fleet), so speech stays clean and there's no feedback howl.
 */
import { levelFromRms } from './mesh.ts';

/** Request the local microphone. Throws on denial — the caller falls back to listen-only. */
export async function getMicStream(micId?: string): Promise<MediaStream> {
  const constraints: MediaStreamConstraints = {
    video: false,
    audio: {
      deviceId: micId ? { exact: micId } : undefined,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  };
  return navigator.mediaDevices.getUserMedia(constraints);
}

/** List the available microphones (labels only populate after permission). */
export async function listMics(): Promise<MediaDeviceInfo[]> {
  const all = await navigator.mediaDevices.enumerateDevices();
  return all.filter((d) => d.kind === 'audioinput');
}

/** Stop every track on a stream and let the OS microphone indicator go out. */
export function stopStream(stream?: MediaStream | null): void {
  if (!stream) return;
  for (const t of stream.getTracks()) t.stop();
}

/** iPhone/iPod (not iPad) routes live-mic audio to the earpiece — a WebKit fact,
 *  not a bug, and no web API can move it. We surface a gentle note when detected. */
export function isIPhone(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /\b(iPhone|iPod)\b/.test(ua);
}

/**
 * Taps an audio stream with an AnalyserNode to expose a smoothed 0..1 level for
 * a speaker ring. Never connected to the destination, so tapping the local mic
 * causes no echo. Uses one shared AudioContext.
 */
export class AudioMeter {
  private analyser: AnalyserNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private buf: Float32Array<ArrayBuffer>;
  private lastRms = 0;

  constructor(ctx: AudioContext, stream: MediaStream) {
    this.buf = new Float32Array(1024);
    try {
      if (stream.getAudioTracks().length > 0) {
        this.source = ctx.createMediaStreamSource(stream);
        this.analyser = ctx.createAnalyser();
        this.analyser.fftSize = 2048;
        this.source.connect(this.analyser);
      }
    } catch {
      /* no audio track — level stays 0 */
    }
  }

  /** Current perceptual level 0..1. */
  read(): number {
    if (!this.analyser) return 0;
    this.analyser.getFloatTimeDomainData(this.buf);
    let sum = 0;
    for (let i = 0; i < this.buf.length; i++) sum += this.buf[i] * this.buf[i];
    this.lastRms = Math.sqrt(sum / this.buf.length);
    return levelFromRms(this.lastRms);
  }

  close(): void {
    try {
      this.source?.disconnect();
      this.analyser?.disconnect();
    } catch {
      /* already gone */
    }
    this.analyser = null;
    this.source = null;
  }
}
